"use client"

import React, { useEffect, useState } from "react"
import SubCard from "../../ui-component/cards/SubCard"
import {
  Autocomplete,
  Box,
  Grid,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material"
import { Table, TableContainer } from "@material-ui/core"
import Collapse from "@mui/material/Collapse"
import IconButton from "@mui/material/IconButton"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import moment from "moment"
import { numericCommaSeparation } from "../../ui-component/NumericSeperation"
import { LoadingButton } from "@material-ui/lab"
import { Link } from "react-router-dom"
import Chip from "../../ui-component/extended/Chip"
import axios from "axios"

// Helper function to get query parameters
const getQueryParams = (key) => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(key)
}

// Payment method label — 1 cash, 2 prepaid/online, 6 partial (see BulkOrder schema comment
// for the fuller code list: 3 Rupifi, 4 Venue, 5 mela/pinelabs)
const PAYMENT_METHOD_LABELS = { 1: "Cash", 2: "Prepaid", 6: "Partial" }
function paymentMethodLabel(paymentMethod) {
  return PAYMENT_METHOD_LABELS[paymentMethod] || (paymentMethod ?? "-")
}

// Cash orders don't populate remainingAmountForCod (that field only gets set for
// paymentMethod 6/partial) — the whole totalPayableAmount IS the COD/cash-on-delivery
// amount in that case, so show it there instead of a misleading 0.
function codAmountValue(order) {
  if (order?.paymentMethod === 1) return order?.totalPayableAmount || 0
  return order?.remainingAmountForCod || 0
}

// Order status chip component
function OrderStatusChip({ status, awbNumber }) {
  if (status === 1 && awbNumber) {
    return <Chip label="In Transit" size="small" chipcolor="primary" />
  } else if (status === 1) {
    return <Chip label="Processing" size="small" chipcolor="primary" />
  } else if (status === 2) {
    return <Chip label="Canceled" size="small" chipcolor="orange" />
  } else if (status === 3) {
    return <Chip label="Complete" size="small" chipcolor="success" />
  } else if (status === 4) {
    return <Chip label="RTO" size="small" chipcolor="orange" />
  } else if (status === 6) {
    return <Chip label="Undelivered" size="small" chipcolor="orange" />
  } else if (status === 8) {
    return <Chip label="Splitted" size="small" chipcolor="secondary" />
  }
  return null
}

// Search button component
function SearchButton({ orderId, allChildOrderShipped, orderStage }) {
  const getSearchUrl = () => {
    if (allChildOrderShipped && orderStage === 3) {
      return `/crm/bulk/bulkshipstatus?orderId=${orderId}`
    } else if (!allChildOrderShipped && orderStage === 3) {
      return `/crm/bulk/bulkorderlist?orderId=${orderId}`
    } else {
      return `/crm/bulk/bulkorderapprovalforscm?orderId=${orderId}`
    }
  }

  const buttonStyle = {
    display: "inline-block",
    padding: "10px 20px",
    backgroundColor: "#007bff",
    color: "white",
    fontSize: "16px",
    textDecoration: "none",
    borderRadius: "25px",
    boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
    transition: "all 0.3s ease-in-out",
  }

  return (
    <a
      target="_blank"
      style={buttonStyle}
      onMouseOver={(e) => (e.target.style.backgroundColor = "#0056b3")}
      onMouseOut={(e) => (e.target.style.backgroundColor = "#007bff")}
      href={getSearchUrl()}
      rel="noreferrer"
    >
      Search
    </a>
  )
}

// Order details link component
function OrderDetailsLink({ orderId, paymentMethod }) {
  return (
    <Link
      to={`/bulk/bulkorderdetails/${orderId}`}
      style={{
        color: paymentMethod === 1 ? "#000" : "green",
      }}
      target="_blank"
    >
      Details
    </Link>
  )
}

// API functions
const fetchAgents = async () => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_PATH_BULK}/admin/getBulkAgents`)

    if (response.data.success) {
      return response.data.data || []
    }
    return []
  } catch (error) {
    console.error("Error fetching agents:", error)
    return []
  }
}

const searchBulkOrders = async (params) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_PATH_BULK}/sales/getAllDetailedCrmBulkOrders`, params)
    return response.data
  } catch (error) {
    console.error("Error searching bulk orders:", error)
    throw error
  }
}

// Process order data to calculate totals and organize nested orders
const processOrderData = (data) => {
  return data.map((order) => {
    let totalDispatchAmount = 0

    // Calculate total dispatch amount
    order.secondLevelChildren.forEach((item) => {
      totalDispatchAmount += item?.awbNumber ? item?.totalPayableAmount : 0
    })

    // Organize nested orders
    order.firstLevelChildren.forEach((item1) => {
      item1.nextOrder = []
      order.secondLevelChildren.forEach((item2) => {
        if (item1.numericOrderId === item2.parentBulkNumericOrderId) {
          item1.nextOrder.push(item2)
        }
      })
    })

    return { ...order, totalDispatchAmount }
  })
}

// Extracted search filters component
function SearchFilters({
  dateRange,
  onDateChange,
  filters,
  onFilterChange,
  agentsData,
  selectedAgents,
  setSelectedAgents,
  onSearch,
  loading,
  orderId,
}) {
  return (
    <>
      <Grid container spacing={2}>
        <Grid item lg={3}>
          <TextField
            label="Start Date"
            type="date"
            value={dateRange.startDate}
            InputLabelProps={{ shrink: true }}
            fullWidth
            onChange={(e) => onDateChange("startDate", e.target.value)}
          />
        </Grid>
        <Grid item lg={3}>
          <TextField
            label="End Date"
            type="date"
            value={dateRange.endDate}
            InputLabelProps={{ shrink: true }}
            fullWidth
            onChange={(e) => onDateChange("endDate", e.target.value)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={2} alignItems="center">
        <Grid item lg={3} md={3}>
          <TextField
            label="Customer Name"
            value={filters.customerName}
            onChange={(e) => onFilterChange("customerName", e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item lg={3} md={3}>
          <TextField
            label="Invoice Id"
            value={filters.invoiceId}
            onChange={(e) => onFilterChange("invoiceId", e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item lg={3} md={3}>
          <TextField
            label="Any Order Id"
            value={filters.numericOrderId}
            onChange={(e) => onFilterChange("numericOrderId", e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item lg={3} md={3}>
          <TextField
            label="AWB Number"
            value={filters.awbNumber || ""}
            onChange={(e) => onFilterChange("awbNumber", e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item lg={3} md={3}>
          <TextField
            label="Phone Number"
            value={filters.phoneNumber}
            onChange={(e) => onFilterChange("phoneNumber", e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item lg={3}>
          <Autocomplete
            options={agentsData}
            fullWidth
            multiple
            getOptionLabel={(option) => `${option.name} (${option.userName})`}
            onChange={(e, v) => setSelectedAgents(v)}
            renderInput={(params) => <TextField {...params} label="Select Agents" placeholder="Select Agents" />}
            value={selectedAgents}
          />
        </Grid>
        <Grid item lg={3}>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={onSearch}
            fullWidth
            loading={loading}
            loadingIndicator="Loading..."
          >
            Search
          </LoadingButton>
        </Grid>
        {orderId && (
          <Grid item lg={3}>
            <LoadingButton
              variant="contained"
              color="secondary"
              component={Link}
              to={`/bulk/bulkorderdetails/${orderId}`}
              target="_blank"
              rel="noreferrer"
              fullWidth
            >
              Details
            </LoadingButton>
          </Grid>
        )}
      </Grid>
    </>
  )
}

// Dispatched order row (no collapsible content)
function DispatchedOrderRow({ order, mainOrder }) {
  const getOrderStatus = (status, awbNumber) => {
    if (awbNumber && status === 1) return "In Transit"
    if (status === 1) return "Processing"
    if (status === 2) return "Cancelled"
    if (status === 3) return "Delivered"
    if (status === 4) return "RTO"
    if (status === 8) return "Splited"
    return ""
  }

  return (
    <TableRow style={{ backgroundColor: order?.isReplacementOrder ? "#e1bee7" : "inherit" }}>
      <TableCell></TableCell>
      <TableCell>{order?.numericOrderId || ""}</TableCell>
      <TableCell>{numericCommaSeparation(order?.totalPayableAmount || 0)}</TableCell>
      <TableCell>{paymentMethodLabel(order?.paymentMethod)}</TableCell>
      <TableCell>{numericCommaSeparation(Math.round(order?.prepaidCollectedAmount || 0))}</TableCell>
      <TableCell>{numericCommaSeparation(Math.round(codAmountValue(order)))}</TableCell>
      <TableCell>{order?.zohoInvoice?.zohoInvoiceNumber || ""}</TableCell>
      <TableCell>{numericCommaSeparation(order?.zohoInvoice?.total || 0)}</TableCell>
      <TableCell>{order?.zohoInvoice?.creditNoteNumber || ""}</TableCell>
      <TableCell>{order?.awbNumber || ""}</TableCell>
      <TableCell>{order?.appointmentDate ? moment(order?.appointmentDate).format('mm:hh A - DD/MM/YYYY'):''}</TableCell>
      <TableCell>{getOrderStatus(order.status, order.awbNumber)}</TableCell>
      <TableCell>
        <OrderDetailsLink orderId={order._id} paymentMethod={order.paymentMethod} />
      </TableCell>
    </TableRow>
  )
}

// Split order row with collapsible dispatched orders
function SplitOrderRow({ order, mainOrder, index }) {
  const [open, setOpen] = useState(false)
  const hasNextOrders = order.nextOrder && order.nextOrder.length > 0

  const getOrderStatus = (status, awbNumber) => {
    if (awbNumber && status === 1) return "In Transit"
    if (status === 1) return "Processing"
    if (status === 2) return "Cancelled"
    if (status === 3) return "Delivered"
    if (status === 4) return "RTO"
    if (status === 8) return "Splited"
    return ""
  }

  return (
    <React.Fragment>
      <TableRow style={{ backgroundColor: order?.isReplacementOrder ? "#e1bee7" : "inherit" }}>
        <TableCell>
          {hasNextOrders && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{`Order Split ${index + 1}`}</TableCell>
        <TableCell>{mainOrder?.name || ""}</TableCell>
        <TableCell>{mainOrder?.phoneNumber || ""}</TableCell>
        <TableCell>{order?.numericOrderId || ""}</TableCell>
        <TableCell>{numericCommaSeparation(Math.round(order?.totalPayableAmount || 0))}</TableCell>
        <TableCell>{paymentMethodLabel(order?.paymentMethod)}</TableCell>
        <TableCell>{numericCommaSeparation(Math.round(order?.prepaidCollectedAmount || 0))}</TableCell>
        <TableCell>{numericCommaSeparation(Math.round(codAmountValue(order)))}</TableCell>
        <TableCell>{order?.appointmentDate && moment(order?.appointmentDate).format('mm:hh A - DD/MM/YYYY')}</TableCell>
        <TableCell>{getOrderStatus(order.status, order.awbNumber)}</TableCell>
        <TableCell>
          <SearchButton
            orderId={order.numericOrderId}
            allChildOrderShipped={order.allChildOrderShipped}
            orderStage={order.orderStage}
          />
        </TableCell>
        <TableCell>
          <OrderDetailsLink orderId={order._id} paymentMethod={order.paymentMethod} />
        </TableCell>
      </TableRow>

      {hasNextOrders && (
        <TableRow style={{ backgroundColor: "#fef6f5" }}>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={13}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box>
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      Order {index + 1} Dispatched Summary
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>Order Id</TableCell>
                    <TableCell>Total PayableAmount</TableCell>
                    <TableCell>Payment Method</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell>COD Amount</TableCell>
                    <TableCell>Invoice Id</TableCell>
                    <TableCell>Invoice Total</TableCell>
                    <TableCell>Cn Number</TableCell>
                    <TableCell>AWB Number</TableCell>
                    <TableCell>Appointment Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.nextOrder.map((item) => (
                    <DispatchedOrderRow key={item.numericOrderId} order={item} mainOrder={order} />
                  ))}
                </TableBody>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  )
}

// Second level children table (dispatched orders)
function SecondLevelChildrenTable({ order }) {
  return (
    <Box>
      <TableHead>
        <TableRow>
          <TableCell></TableCell>
          <TableCell>Order Id</TableCell>
          <TableCell>Total PayableAmount</TableCell>
          <TableCell>Payment Method</TableCell>
          <TableCell>Paid</TableCell>
          <TableCell>COD Amount</TableCell>
          <TableCell>Invoice Id</TableCell>
          <TableCell>Invoice Total</TableCell>
          <TableCell>Cn Number</TableCell>
          <TableCell>AWB Number</TableCell>
          <TableCell>Appointment Date</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Details</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {order.secondLevelChildren.map((item) => (
          <DispatchedOrderRow key={item.numericOrderId} order={item} mainOrder={order} />
        ))}
      </TableBody>
    </Box>
  )
}

// First level children table (split orders)
function FirstLevelChildrenTable({ order }) {
  return (
    <Box>
      <TableHead>
        <TableRow>
          <TableCell></TableCell>
          <TableCell>Order</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>Phone Number</TableCell>
          <TableCell>Order Id</TableCell>
          <TableCell>Total PayableAmount</TableCell>
          <TableCell>Payment Method</TableCell>
          <TableCell>Paid</TableCell>
          <TableCell>COD Amount</TableCell>
          <TableCell>Appointment Date</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Find Order</TableCell>
          <TableCell>Details</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {order.firstLevelChildren.map((item, index) => (
          <SplitOrderRow key={item.numericOrderId} order={item} mainOrder={order} index={index} />
        ))}
      </TableBody>
    </Box>
  )
}

// Order row component with collapsible details
function OrderRow({ order }) {
  const [open, setOpen] = useState(false)
  const hasChildren = order.firstLevelChildren.length > 0 || order.secondLevelChildren.length > 0

  return (
    <React.Fragment>
      <TableRow style={{ backgroundColor: order?.isReplacementOrder ? "#e1bee7" : "inherit" }}>
        <TableCell>
          {hasChildren && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{order?.name || ""}</TableCell>
        <TableCell>{order?.numericOrderId || ""}</TableCell>
        <TableCell>{order?.phoneNumber || ""}</TableCell>
        <TableCell>{moment(order?.createdAt).format("hh:mm a DD-MMM-YYYY")}</TableCell>
        <TableCell>{order?.agentName || ""}</TableCell>
        <TableCell>{order?.immediateSeniorName || ""}</TableCell>
        <TableCell>
          <OrderStatusChip status={order.status} awbNumber={order.awbNumber} />
        </TableCell>
        <TableCell>{numericCommaSeparation(Math.round(order?.totalPayableAmount || 0))}</TableCell>
        <TableCell>{paymentMethodLabel(order?.paymentMethod)}</TableCell>
        <TableCell>{numericCommaSeparation(Math.round(order?.prepaidCollectedAmount || 0))}</TableCell>
        <TableCell>{numericCommaSeparation(Math.round(codAmountValue(order)))}</TableCell>
        <TableCell>{numericCommaSeparation(Math.round(order?.totalDispatchAmount || 0))}</TableCell>
        <TableCell>
          <SearchButton
            orderId={order.numericOrderId}
            allChildOrderShipped={order.allChildOrderShipped}
            orderStage={order.orderStage}
          />
        </TableCell>
        <TableCell>
          <OrderDetailsLink orderId={order._id} paymentMethod={order.paymentMethod} />
        </TableCell>
      </TableRow>

      {hasChildren && (
        <TableRow style={{ backgroundColor: "#e7e4e4" }}>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={15}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              {order.firstLevelChildren.length > 0 ? (
                <FirstLevelChildrenTable order={order} />
              ) : (
                order.secondLevelChildren.length > 0 && <SecondLevelChildrenTable order={order} />
              )}
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  )
}

// Main orders table component
function OrdersTable({ orders, pagination, onPageChange, onRowsPerPageChange }) {
  return (
    <>
      <TableContainer style={{ maxHeight: 440, marginTop: 20 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Order Id</TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>Order Date</TableCell>
              <TableCell>Agent Name</TableCell>
              <TableCell>Immediate Senior</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Total PayableAmount</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Paid</TableCell>
              <TableCell>COD Amount</TableCell>
              <TableCell>Total Dispatch Amount</TableCell>
              <TableCell>Search</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <OrderRow key={order.numericOrderId} order={order} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={pagination.totalCount || 0}
        rowsPerPage={pagination.rowsPerPage}
        page={pagination.page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </>
  )
}

// Main component
function SearchBulkOrders() {
  // Get order ID from URL if present
  const routerOrderId = getQueryParams("orderId")

  // Date state with more meaningful defaults
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(2, "M").startOf("month").format("YYYY-MM-DD"),
    endDate: new Date().toISOString().slice(0, 10),
  })

  // Search filters state
  const [filters, setFilters] = useState({
    customerName: "",
    invoiceId: "",
    phoneNumber: "",
    numericOrderId: routerOrderId || "",
    awbNumber: "",
  })

  // Agents data state
  const [agentsData, setAgentsData] = useState([])
  const [selectedAgents, setSelectedAgents] = useState([])

  // Results state
  const [orders, setOrders] = useState([])
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 25,
    totalCount: 0,
  })
  const [loading, setLoading] = useState(false)
  const [orderId, setOrderId] = useState(null)

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  // Handle date changes
  const handleDateChange = (field, value) => {
    setDateRange((prev) => ({ ...prev, [field]: value }))
  }

  // Handle search with pagination
  const handleSearch = async (page = pagination.page, limit = pagination.rowsPerPage) => {
    setLoading(true)

    try {
      const response = await searchBulkOrders({
        customerName: filters.customerName,
        invoiceNumber: filters.invoiceId,
        phoneNumber: filters.phoneNumber,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        agnetIds: selectedAgents.map((item) => item._id),
        numericOrderId: filters.numericOrderId,
        awbNumber: filters.awbNumber,
        page,
        limit,
      })

      if (response.success) {
        const processedData = processOrderData(response.data[0]?.data || [])
        setOrders(processedData)
        setPagination((prev) => ({
          ...prev,
          totalCount: response.data[0]?.metaData[0]?.totalDocuments || 0,
        }))
        // Set orderId from first result if available (for Details button)
        if (processedData.length > 0 && processedData[0]._id) {
          setOrderId(processedData[0]._id)
        } else {
          setOrderId(null)
        }
      } else {
        alert(response.message)
      }
    } catch (error) {
      console.error(error)
      alert(error?.response?.data?.message || "Something went wrong")
    } finally {
      setLoading(false)
    }
  }

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }))
    handleSearch(newPage, pagination.rowsPerPage)
  }

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = +event.target.value
    setPagination((prev) => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }))
    handleSearch(0, newRowsPerPage)
  }

  // Fetch agents on component mount
  useEffect(() => {
    const getAgents = async () => {
      try {
        const data = await fetchAgents()
        if (data.length > 0) {
          setAgentsData(data)
        }
      } catch (error) {
        console.error("Failed to fetch agents:", error)
      }
    }

    getAgents()

    // If we have an order ID from the URL, search immediately
    if (routerOrderId) {
      handleSearch()
    }
  }, [])

  return (
    <SubCard>
      <SearchFilters
        dateRange={dateRange}
        onDateChange={handleDateChange}
        filters={filters}
        onFilterChange={handleFilterChange}
        agentsData={agentsData}
        selectedAgents={selectedAgents}
        setSelectedAgents={setSelectedAgents}
        onSearch={() => handleSearch()}
        loading={loading}
        orderId={orderId}
      />

      <OrdersTable
        orders={orders}
        pagination={pagination}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </SubCard>
  )
}

export default SearchBulkOrders
